import { nextTick } from 'vue';

/**
 * 전체 창 스크롤을 최상단으로 이동
 */
export const setScrollTopForFullWindow = () => {
  const windowEl = document.getElementsByClassName('window--type-full');
  const targetEl = windowEl[0]?.getElementsByClassName('ev-window-content');
  if (targetEl && targetEl[0]) {
    targetEl[0].scrollTop = 0;
  }
};

/**
 * 선택한 셀렉터의 요소를 하단으로 스크롤
 * @param selectors CSS 셀렉터
 */
export const scrollToBottom = (selectors: string) => {
  const windowContentEl = window.document.querySelector(selectors);
  if (windowContentEl) {
    nextTick(() => {
      windowContentEl.scrollTo({
        top: windowContentEl.scrollHeight,
        left: 0,
        behavior: 'smooth',
      });
    });
  }
};

/**
 * CSS 변수 값을 가져옴
 * @param varName CSS 변수명
 * @returns 변수 값
 */
export const getCSSVariable = (varName: string) => {
  let cssVariableName = varName;
  const match = varName?.match(/var\((--[^)]+)\)/);
  if (match && varName) {
    cssVariableName = match[1].trim();
    return getComputedStyle(document.documentElement).getPropertyValue(cssVariableName).trim();
  }
  return varName;
};

interface MeasureTextOptions {
  fontWeight?: string;
  letterSpacing?: string;
}

// 측정 전용 canvas — 호출마다 canvas를 생성하지 않도록 모듈 스코프에 1개만 보관 (undefined = 미생성, null = 생성 실패)
let measureContext: CanvasRenderingContext2D | null | undefined;

const getMeasureContext = () => {
  if (measureContext === undefined) {
    measureContext = document.createElement('canvas').getContext('2d');
  }
  return measureContext;
};

/**
 * 텍스트의 실제 렌더링 너비를 측정
 * @param text 텍스트
 * @param fontSize 폰트 크기
 * @param fontFamily 폰트 패밀리
 * @param options 측정 정밀도 옵션 (fontWeight, letterSpacing)
 * @returns 텍스트 너비(px)
 */
export const getMeasuredTextWidth = (
  text: string,
  fontSize = '12px',
  fontFamily = 'Pretendard',
  options: MeasureTextOptions = {},
): number => {
  const context = getMeasureContext();
  if (!context) {
    return 0;
  }
  const { fontWeight, letterSpacing } = options;
  context.font = [fontWeight, fontSize, fontFamily].filter(Boolean).join(' ');
  // context가 재사용되므로 매 호출 초기화 필수. CSS 'normal'은 canvas letterSpacing에서 무시되므로 '0px'로 정규화
  context.letterSpacing = !letterSpacing || letterSpacing === 'normal' ? '0px' : letterSpacing;
  const metrics = context.measureText(text);
  return metrics.width;
};

interface PopupPositionOptions {
  gap?: number;
  align?: 'left' | 'right';
}

/**
 * teleport 팝업의 position: fixed 좌표를 계산
 * @param triggerRect 트리거 요소의 DOMRect
 * @param popupSize 팝업의 { width, height }
 * @param windowSize 뷰포트의 { width, height }
 * @param options gap: 트리거와 팝업 사이 간격(px), align: 팝업 좌우 정렬 기준
 */
export const calculatePopupPosition = (
  triggerRect: DOMRect,
  popupSize: { width: number; height: number },
  windowSize: { width: number; height: number },
  options: PopupPositionOptions = {},
) => {
  const { gap = 0, align = 'left' } = options;

  const topValue = triggerRect.top + triggerRect.height + gap;
  let top: string;
  let bottom: string;

  if (topValue + popupSize.height > windowSize.height) {
    bottom = `${windowSize.height - triggerRect.top + gap}px`;
    top = 'auto';
  } else {
    top = `${topValue}px`;
    bottom = 'auto';
  }

  let left: string;
  let right: string;

  if (align === 'right') {
    const leftValue = triggerRect.right - popupSize.width;
    left = leftValue < 0 ? '10px' : `${leftValue}px`;
    right = 'auto';
  } else {
    const isWidthOverflow = triggerRect.left + popupSize.width > windowSize.width;
    left = isWidthOverflow ? 'auto' : `${triggerRect.left}px`;
    right = isWidthOverflow ? '10px' : 'auto';
  }

  return { top, bottom, left, right };
};

// 비출력 ASCII 문자(제어문자) 정규식
// eslint-disable-next-line no-control-regex
export const NON_PRINTABLE_ASCII_REGEX = /[\x00-\x1F\x7F]/g;
/**
 * DOM 트리 내 텍스트 노드의 제어문자를 공백으로 치환
 * @param element HTMLElement
 */
export const replaceTextNodes = (element: HTMLElement) => {
  element.childNodes.forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      node.textContent = node.textContent?.replace(NON_PRINTABLE_ASCII_REGEX, ' ') || '';
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      replaceTextNodes(node as HTMLElement);
    }
  });
};
